import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import { PlusIcon, CalendarDaysIcon, CheckCircleIcon, XCircleIcon, PencilIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const SeasonsContainer = styled.div`
  padding: 1.5rem;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  background: ${theme.colors.background.paper};
  border-radius: 0.5rem;
  padding: 1.5rem;
  border: 1px solid ${theme.colors.border.primary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${theme.typography.fontFamily.primary};
`;

const CreateSeasonButton = styled.button`
  background: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrast};
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  font-family: ${theme.typography.fontFamily.primary};

  &:hover {
    background: ${theme.colors.primary.dark};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

const SeasonsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const SeasonCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily.primary};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  ${props => props.isActive && `
    border-color: ${theme.colors.success.main};
    box-shadow: 0 0 0 2px ${theme.colors.success.main};
  `}
`;

const SeasonCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SeasonName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const SeasonStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: ${theme.typography.fontFamily.primary};

  ${props => props.status === 'active' && `
    background: ${theme.colors.success.light};
    color: ${theme.colors.success.dark};
  `}
  ${props => props.status === 'completed' && `
    background: ${theme.colors.secondary.light};
    color: ${theme.colors.secondary.dark};
  `}
  ${props => props.status === 'closed' && `
    background: ${theme.colors.neutral[200]};
    color: ${theme.colors.neutral[700]};
  `}
`;

const SeasonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const SeasonActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.secondary};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily.primary};

  &:hover {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
    background: ${theme.colors.background.secondary};
  }

  &.primary {
    background: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrast};
    border-color: ${theme.colors.primary.main};

    &:hover {
      background: ${theme.colors.primary.dark};
      border-color: ${theme.colors.primary.dark};
      transform: translateY(-1px);
    }
  }

  &.danger {
    background: ${theme.colors.error.main};
    color: ${theme.colors.error.contrast};
    border-color: ${theme.colors.error.main};

    &:hover {
      background: ${theme.colors.error.dark};
      border-color: ${theme.colors.error.dark};
      transform: translateY(-1px);
    }
  }

  &.success {
    background: ${theme.colors.success.main};
    color: ${theme.colors.success.contrast};
    border-color: ${theme.colors.success.main};

    &:hover {
      background: ${theme.colors.success.dark};
      border-color: ${theme.colors.success.dark};
      transform: translateY(-1px);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily.primary};
  
  svg {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem;
    color: ${theme.colors.text.disabled};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.paper};
  padding: 2rem;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  width: 100%;
  max-width: 500px;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0;
  font-family: ${theme.typography.fontFamily.primary};

  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.input.background};
  color: ${theme.colors.input.text};
  font-size: 1rem;
  font-family: ${theme.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 1px ${theme.colors.primary.main};
  }

  &::placeholder {
    color: ${theme.colors.input.placeholder};
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.input.background};
  color: ${theme.colors.input.text};
  font-size: 1rem;
  font-family: ${theme.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 1px ${theme.colors.primary.main};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const VesselSeasonsTab = ({ vesselId }) => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [form, setForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
    status: 'active'
  });

  useEffect(() => {
    if (vesselId) {
      fetchSeasons();
    }
  }, [vesselId]);

  const fetchSeasons = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/vessel-seasons/vessel/${vesselId}`);
      setSeasons(response.data.seasons || []);
    } catch (error) {
      console.error('Error fetching seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSeason = async () => {
    try {
      await api.post(`/api/vessel-seasons/vessel/${vesselId}`, form);
      toast.success('Season created successfully!');
      fetchSeasons();
      setShowModal(false);
    } catch (error) {
      console.error('Error creating season:', error);
      toast.error(error.response?.data?.error || 'Failed to create season');
    }
  };

  const handleUpdateSeason = async () => {
    try {
      await api.put(`/api/vessel-seasons/${currentSeason.id}`, form);
      toast.success('Season updated successfully!');
      fetchSeasons();
      setShowModal(false);
    } catch (error) {
      console.error('Error updating season:', error);
      toast.error(error.response?.data?.error || 'Failed to update season');
    }
  };

  const handleDeleteSeason = async (id) => {
    if (window.confirm('Are you sure you want to delete this season?')) {
      try {
        await api.delete(`/api/vessel-seasons/${id}`);
        toast.success('Season deleted successfully!');
        fetchSeasons();
      } catch (error) {
        console.error('Error deleting season:', error);
        toast.error('Failed to delete season');
      }
    }
  };

  const handleCloseSeason = async (id) => {
    if (window.confirm('Close this season and generate summary report?')) {
      try {
        const response = await api.post(`/api/vessel-seasons/${id}/close`);
        toast.success('Season closed successfully!');
        fetchSeasons();
        
        // Show summary report
        const summary = response.data;
        console.log('Season Summary Report:', summary);
        // TODO: Show summary in a modal or new page
      } catch (error) {
        console.error('Error closing season:', error);
        toast.error('Failed to close season');
      }
    }
  };

  const openCreateModal = () => {
    setCurrentSeason(null);
    setForm({
      name: '',
      year: new Date().getFullYear(),
      start_date: '',
      end_date: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const openEditModal = (season) => {
    setCurrentSeason(season);
    setForm({
      name: season.name,
      year: season.year,
      start_date: season.start_date,
      end_date: season.end_date,
      status: season.status
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'completed':
        return <XCircleIcon className="w-4 h-4" />;
      case 'closed':
        return <DocumentTextIcon className="w-4 h-4" />;
      default:
        return <CalendarDaysIcon className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <SeasonsContainer>
        <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
          Loading seasons...
        </div>
      </SeasonsContainer>
    );
  }

  return (
    <SeasonsContainer>
      <Header>
        <Title>
          <CalendarDaysIcon className="w-6 h-6" />
          Vessel Seasons
        </Title>
        <CreateSeasonButton onClick={openCreateModal}>
          <PlusIcon className="w-5 h-5" />
          Create Season
        </CreateSeasonButton>
      </Header>

      {seasons.length === 0 ? (
        <EmptyState>
          <CalendarDaysIcon className="w-16 h-16" />
          <h3>No Seasons Yet</h3>
          <p>Create your first season to start tracking trips and statistics.</p>
        </EmptyState>
      ) : (
        <SeasonsList>
          {seasons.map((season) => (
            <SeasonCard key={season.id} isActive={season.status === 'active'}>
              <SeasonCardHeader>
                <SeasonName>{season.name}</SeasonName>
                <SeasonStatus status={season.status}>
                  {getStatusIcon(season.status)}
                  {season.status}
                </SeasonStatus>
              </SeasonCardHeader>
              
              <SeasonDetails>
                <div>Year: {season.year}</div>
                <div>Start: {new Date(season.start_date).toLocaleDateString()}</div>
                <div>End: {new Date(season.end_date).toLocaleDateString()}</div>
              </SeasonDetails>

              <SeasonActions>
                <ActionButton onClick={() => openEditModal(season)}>
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </ActionButton>
                
                {season.status === 'active' && (
                  <ActionButton 
                    className="success" 
                    onClick={() => handleCloseSeason(season.id)}
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    Close
                  </ActionButton>
                )}
                
                <ActionButton 
                  className="danger" 
                  onClick={() => handleDeleteSeason(season.id)}
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </ActionButton>
              </SeasonActions>
            </SeasonCard>
          ))}
        </SeasonsList>
      )}

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {currentSeason ? 'Edit Season' : 'Create New Season'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            <FormGroup>
              <Label>Season Name</Label>
              <Input
                type="text"
                name="name"
                value={form.name}
                onChange={handleFormChange}
                placeholder="e.g., 2025 Season"
              />
            </FormGroup>

            <FormGroup>
              <Label>Year</Label>
              <Input
                type="number"
                name="year"
                value={form.year}
                onChange={handleFormChange}
                min="2020"
                max="2030"
              />
            </FormGroup>

            <FormGroup>
              <Label>Start Date</Label>
              <DateInput
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={handleFormChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>End Date</Label>
              <DateInput
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={handleFormChange}
              />
            </FormGroup>

            <ModalActions>
              <ActionButton onClick={() => setShowModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton 
                className="primary" 
                onClick={currentSeason ? handleUpdateSeason : handleCreateSeason}
              >
                {currentSeason ? 'Update' : 'Create'} Season
              </ActionButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </SeasonsContainer>
  );
};

export default VesselSeasonsTab;